// src/components/ProjectManagement/DataAppraisal.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';

// Global cache
let cachedData = {
  projects: [],
  tasks: [],
  workers: [],
  lastFetch: 0
};
let isFetching = false;
let fetchPromise = null;
const CACHE_DURATION = 60000; // 60 seconds cache

const DataAppraisal = ({ currentUser, userRole }) => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState(cachedData.projects);
  const [workers, setWorkers] = useState(cachedData.workers);
  const [tasks, setTasks] = useState(cachedData.tasks);
  const [selectedMetric, setSelectedMetric] = useState('projects');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Helper: Format date
  const formatDate = useCallback((date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString();
  }, []);

  // Helper: Get status color
  const getStatusColor = useCallback((status) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'ongoing': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  }, []);

  // OPTIMIZED: Fetch all data with caching and parallel execution
  const fetchAllData = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && cachedData.projects.length > 0 && (now - cachedData.lastFetch) < CACHE_DURATION) {
      setProjects(cachedData.projects);
      setTasks(cachedData.tasks);
      setWorkers(cachedData.workers);
      setLoading(false);
      return cachedData;
    }
    
    // Prevent multiple simultaneous fetches
    if (isFetching) {
      if (fetchPromise) {
        const result = await fetchPromise;
        setProjects(result.projects);
        setTasks(result.tasks);
        setWorkers(result.workers);
        setLoading(false);
        return result;
      }
      return;
    }
    
    isFetching = true;
    setLoading(true);
    
    fetchPromise = (async () => {
      try {
        // OPTIMIZED: Fetch all collections in PARALLEL
        const [projectsSnapshot, tasksSnapshot, workersSnapshot] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'tasks')),
          getDocs(collection(db, 'users'))
        ]);
        
        // Process projects
        const projectsData = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
          startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
          createdAtObj: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || 0)
        }));
        
        // Process tasks
        const tasksData = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          endDateObj: doc.data().endDate?.toDate?.() || (doc.data().endDate ? new Date(doc.data().endDate) : null),
          completedAtObj: doc.data().completedAt?.toDate?.() || null,
          dueDateObj: doc.data().deadline?.toDate?.() || null,
          startDateObj: doc.data().startDate?.toDate?.() || (doc.data().startDate ? new Date(doc.data().startDate) : null),
          createdAtObj: doc.data().createdAt?.toDate?.() || new Date()
        }));
        
        // Process workers
        const workersData = workersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Update cache
        cachedData = {
          projects: projectsData,
          tasks: tasksData,
          workers: workersData,
          lastFetch: Date.now()
        };
        
        return cachedData;
      } catch (error) {
        console.error('Error fetching data:', error);
        return cachedData;
      } finally {
        isFetching = false;
        fetchPromise = null;
        setLoading(false);
      }
    })();
    
    const result = await fetchPromise;
    setProjects(result.projects);
    setTasks(result.tasks);
    setWorkers(result.workers);
    return result;
  }, []);

  // OPTIMIZED: Debounced real-time listeners
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial load
    fetchAllData();
    
    // Debounced listener for all collections
    const collections = ['projects', 'tasks', 'users'];
    const unsubscribes = collections.map(collectionName => {
      return onSnapshot(collection(db, collectionName), () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            fetchAllData(true);
          }
        }, 1000);
      });
    });
    
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [fetchAllData]);

  // Filter by date range
  const filterByDateRange = useCallback((item) => {
    if (!dateRange.start && !dateRange.end) return true;
    const itemDate = item.createdAtObj || item.endDateObj;
    if (!itemDate) return true;
    if (dateRange.start && itemDate < dateRange.start) return false;
    if (dateRange.end && itemDate > dateRange.end) return false;
    return true;
  }, [dateRange]);

  // OPTIMIZED: Calculate project metrics with single pass
  const projectMetrics = useMemo(() => {
    const now = new Date();
    const filteredProjects = projects.filter(filterByDateRange);
    
    // Create a map for faster task lookups
    const tasksByProjectMap = new Map();
    tasks.forEach(task => {
      if (!tasksByProjectMap.has(task.projectId)) {
        tasksByProjectMap.set(task.projectId, []);
      }
      tasksByProjectMap.get(task.projectId).push(task);
    });
    
    return filteredProjects.map(project => {
      const projectTasks = tasksByProjectMap.get(project.id) || [];
      let completedTasks = 0;
      let pendingTasks = 0;
      let ongoingTasks = 0;
      let overdueTasks = 0;
      
      // Single pass through project tasks
      for (const task of projectTasks) {
        if (task.status === 'completed' && task.qcVerified) {
          completedTasks++;
        } else if (task.status === 'pending') {
          pendingTasks++;
          const endDate = task.endDateObj;
          if (endDate && endDate < now) {
            overdueTasks++;
          }
        } else if (task.status === 'ongoing') {
          ongoingTasks++;
          const endDate = task.endDateObj;
          if (endDate && endDate < now) {
            overdueTasks++;
          }
        }
      }
      
      const allTasksCompleted = projectTasks.length > 0 && completedTasks === projectTasks.length;
      const isFinished = project.status === 'completed' || allTasksCompleted;
      
      const projectEndDate = project.endDateObj;
      let finishedOnTime = false;
      let finishedLate = false;
      
      if (isFinished && projectEndDate) {
        finishedOnTime = projectEndDate >= now;
        finishedLate = projectEndDate < now;
      }
      
      const completionRate = projectTasks.length > 0 ? Math.round((completedTasks / projectTasks.length) * 100) : 0;
      
      return {
        ...project,
        totalTasks: projectTasks.length,
        completedTasks,
        pendingTasks,
        ongoingTasks,
        overdueTasks,
        completionRate,
        isFinished,
        finishedOnTime,
        finishedLate,
        allTasksCompleted
      };
    });
  }, [projects, tasks, filterByDateRange]);

  // OPTIMIZED: Get active tasks with details using Map lookups
  const activeTasks = useMemo(() => {
    const filteredTasks = tasks.filter(filterByDateRange);
    const now = new Date();
    
    // Create maps for O(1) lookups
    const projectMap = new Map(projects.map(p => [p.id, p]));
    const workerMap = new Map(workers.map(w => [w.id, w]));
    
    return filteredTasks
      .filter(t => t.status === 'pending' || t.status === 'ongoing')
      .map(task => {
        const project = projectMap.get(task.projectId);
        let worker = null;
        
        // Handle different assignment formats
        if (Array.isArray(task.assignedTo)) {
          // For multiple assignments, just show count
          worker = task.assignedTo.length > 0 ? { name: `${task.assignedTo.length} workers` } : null;
        } else if (typeof task.assignedTo === 'string') {
          worker = workerMap.get(task.assignedTo);
        } else if (task.assignedTo && typeof task.assignedTo === 'object') {
          const workerIds = Object.values(task.assignedTo);
          worker = workerIds.length > 0 ? { name: `${workerIds.length} workers` } : null;
        }
        
        return {
          id: task.id,
          taskName: task.title,
          taskStatus: task.status,
          projectName: project?.projectName || 'Unknown Project',
          workerName: worker?.name || 'Unassigned',
          workerRole: worker?.role || 'N/A',
          startDate: task.startDateObj,
          endDate: task.endDateObj,
          isOverdue: task.endDateObj && task.endDateObj < now
        };
      });
  }, [tasks, projects, workers, filterByDateRange]);

  // OPTIMIZED: Calculate worker metrics with Map
  const workerMetrics = useMemo(() => {
    const now = new Date();
    const filteredWorkers = workers;
    
    // Create task map by worker ID
    const tasksByWorkerMap = new Map();
    tasks.forEach(task => {
      // Handle different assignment formats
      let workerIds = [];
      if (Array.isArray(task.assignedTo)) {
        workerIds = task.assignedTo;
      } else if (typeof task.assignedTo === 'string') {
        workerIds = [task.assignedTo];
      } else if (task.assignedTo && typeof task.assignedTo === 'object') {
        workerIds = Object.values(task.assignedTo);
      }
      
      workerIds.forEach(workerId => {
        if (!tasksByWorkerMap.has(workerId)) {
          tasksByWorkerMap.set(workerId, []);
        }
        tasksByWorkerMap.get(workerId).push(task);
      });
    });
    
    return filteredWorkers.map(worker => {
      const workerTasks = tasksByWorkerMap.get(worker.id) || [];
      let completedTasks = 0;
      let overdueTasks = 0;
      let onTimeTasks = 0;
      
      // Single pass through worker tasks
      for (const task of workerTasks) {
        if (task.status === 'completed' && task.qcVerified) {
          completedTasks++;
          const endDate = task.endDateObj;
          const completedAt = task.completedAtObj;
          if (endDate && completedAt && completedAt <= endDate) {
            onTimeTasks++;
          }
        } else if (task.status !== 'completed') {
          const endDate = task.endDateObj;
          if (endDate && endDate < now) {
            overdueTasks++;
          }
        }
      }
      
      const onTimeRate = completedTasks > 0 ? Math.round((onTimeTasks / completedTasks) * 100) : null;
      
      return {
        ...worker,
        totalTasks: workerTasks.length,
        completedTasks,
        overdueTasks,
        onTimeRate,
        hasCompletedTasks: completedTasks > 0
      };
    });
  }, [workers, tasks]);

  // Memoized statistics
  const projectsFinishedOnTime = useMemo(() => projectMetrics.filter(p => p.finishedOnTime).length, [projectMetrics]);
  const projectsFinishedLate = useMemo(() => projectMetrics.filter(p => p.finishedLate).length, [projectMetrics]);
  const projectsCompleted = useMemo(() => projectMetrics.filter(p => p.isFinished).length, [projectMetrics]);
  const projectsWithOverdue = useMemo(() => projectMetrics.filter(p => p.overdueTasks > 0).length, [projectMetrics]);
  
  const projectsMostActive = useMemo(() => [...projectMetrics]
    .filter(p => (p.pendingTasks + p.ongoingTasks) > 0)
    .sort((a, b) => (b.pendingTasks + b.ongoingTasks) - (a.pendingTasks + a.ongoingTasks))
    .slice(0, 5), [projectMetrics]);
  
  const completedProjects = useMemo(() => projectMetrics.filter(p => p.isFinished), [projectMetrics]);
  
  const workersWithTasks = useMemo(() => workerMetrics.filter(w => w.totalTasks > 0), [workerMetrics]);
  const workersWithCompletedTasks = useMemo(() => workerMetrics.filter(w => w.hasCompletedTasks), [workerMetrics]);
  const workersWithOverdue = useMemo(() => workerMetrics.filter(w => w.overdueTasks > 0), [workerMetrics]);
  
  const topPerformers = useMemo(() => [...workersWithCompletedTasks]
    .sort((a, b) => (b.onTimeRate || 0) - (a.onTimeRate || 0))
    .slice(0, 5), [workersWithCompletedTasks]);
  
  const mostProductive = useMemo(() => [...workerMetrics]
    .sort((a, b) => b.completedTasks - a.completedTasks)
    .slice(0, 5), [workerMetrics]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    setExporting(true);
    setTimeout(() => {
      try {
        let csvData = [];
        if (selectedMetric === 'projects') {
          csvData = projectMetrics.map(p => ({
            'Project Name': p.projectName,
            'Status': p.status,
            'Total Tasks': p.totalTasks,
            'Completed': p.completedTasks,
            'Pending': p.pendingTasks,
            'Ongoing': p.ongoingTasks,
            'Overdue': p.overdueTasks,
            'Completion %': p.completionRate,
            'Finished On Time': p.finishedOnTime ? 'Yes' : 'No',
            'End Date': formatDate(p.endDateObj)
          }));
        } else {
          csvData = workerMetrics.map(w => ({
            'Worker Name': w.name,
            'Role': w.role,
            'Total Tasks': w.totalTasks,
            'Completed Tasks': w.completedTasks,
            'Overdue Tasks': w.overdueTasks,
            'On-Time Rate': w.onTimeRate ? `${w.onTimeRate}%` : 'N/A'
          }));
        }
        
        const headers = Object.keys(csvData[0] || {});
        const csvRows = [headers.join(',')];
        for (const row of csvData) {
          const values = headers.map(header => {
            const val = row[header];
            return `"${String(val).replace(/"/g, '""')}"`;
          });
          csvRows.push(values.join(','));
        }
        
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `data-appraisal-${selectedMetric}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting:', error);
        alert('Failed to export data');
      } finally {
        setExporting(false);
      }
    }, 100);
  }, [selectedMetric, projectMetrics, workerMetrics, formatDate]);

  const clearDateFilter = useCallback(() => {
    setDateRange({ start: null, end: null });
    setShowDatePicker(false);
  }, []);

  if (loading && cachedData.projects.length === 0) {
    return (
      <div className="data-appraisal-container">
        <div className="skeleton-header"></div>
        <div className="skeleton-metrics">
          <div className="skeleton-card"></div>
          <div className="skeleton-card"></div>
          <div className="skeleton-card"></div>
          <div className="skeleton-card"></div>
        </div>
        <div className="skeleton-content"></div>
      </div>
    );
  }

  return (
    <div className="data-appraisal-container">
      {/* Header with Export Button */}
      <div className="appraisal-header">
        <div className="header-left">
          <h1>📊 Data Appraisal Dashboard</h1>
          <p>Performance analytics, project metrics, and worker efficiency reports</p>
        </div>
        <div className="header-actions">
          <div className="date-filter-wrapper">
            <button 
              className="date-filter-btn"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              📅 {dateRange.start ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}` : 'Filter by Date'}
            </button>
            {showDatePicker && (
              <div className="date-picker-dropdown">
                <div>
                  <label>Start Date</label>
                  <input 
                    type="date" 
                    value={dateRange.start?.toISOString().split('T')[0] || ''}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value ? new Date(e.target.value) : null })}
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input 
                    type="date" 
                    value={dateRange.end?.toISOString().split('T')[0] || ''}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value ? new Date(e.target.value) : null })}
                  />
                </div>
                <button onClick={clearDateFilter} className="clear-filter-btn">Clear</button>
              </div>
            )}
          </div>
          <button className="export-btn" onClick={exportToCSV} disabled={exporting}>
            {exporting ? '📤 Exporting...' : '📥 Export to CSV'}
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="key-metrics-grid">
        <div className="metric-card success" title="Projects that were completed before or on the due date">
          <div className="metric-icon">✅</div>
          <div className="metric-info">
            <span className="metric-value">{projectsFinishedOnTime}</span>
            <span className="metric-label">Projects Finished On Time</span>
          </div>
        </div>
        <div className="metric-card warning" title="Projects that were completed after the due date">
          <div className="metric-icon">⚠️</div>
          <div className="metric-info">
            <span className="metric-value">{projectsFinishedLate}</span>
            <span className="metric-label">Projects Finished Late</span>
          </div>
        </div>
        <div className="metric-card danger" title="Projects that have one or more overdue tasks">
          <div className="metric-icon">🔴</div>
          <div className="metric-info">
            <span className="metric-value">{projectsWithOverdue}</span>
            <span className="metric-label">Projects With Overdue Tasks</span>
          </div>
        </div>
        <div className="metric-card info" title="Total number of projects">
          <div className="metric-icon">📊</div>
          <div className="metric-info">
            <span className="metric-value">{projectMetrics.length}</span>
            <span className="metric-label">Total Projects</span>
          </div>
        </div>
      </div>

      {/* Summary Stats Row */}
      <div className="summary-stats">
        <div className="summary-stat">
          <span className="stat-value">{workers.length}</span>
          <span className="stat-label">Total Team Members</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{workersWithTasks.length}</span>
          <span className="stat-label">Active Workers</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{tasks.length}</span>
          <span className="stat-label">Total Tasks</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{tasks.filter(t => t.status === 'completed').length}</span>
          <span className="stat-label">Completed Tasks</span>
        </div>
      </div>

      <div className="appraisal-section">
        <div className="section-header">
          <h2>📁 Performance Analysis</h2>
          <div className="section-tabs">
            <button className={`section-tab ${selectedMetric === 'projects' ? 'active' : ''}`} onClick={() => setSelectedMetric('projects')}>
              📊 Projects
            </button>
            <button className={`section-tab ${selectedMetric === 'workers' ? 'active' : ''}`} onClick={() => setSelectedMetric('workers')}>
              👥 Workers
            </button>
          </div>
        </div>

        {selectedMetric === 'projects' ? (
          <>
            <div className="appraisal-card">
              <div className="card-header">
                <h3>🚨 Projects Requiring Attention</h3>
                <span className="card-badge warning">Pending + Ongoing Tasks</span>
              </div>
              <div className="card-content">
                {projectsMostActive.length === 0 ? (
                  <div className="empty-message">
                    <span>✅ No active tasks across projects</span>
                    <p>All projects are on track!</p>
                  </div>
                ) : (
                  <div className="ranking-list">
                    {projectsMostActive.map((project, index) => (
                      <div key={project.id} className="ranking-item">
                        <div className="rank">{index + 1}</div>
                        <div className="rank-info">
                          <div className="rank-name">{project.projectName}</div>
                          <div className="rank-stats">
                            <span className="stat pending">⏳ {project.pendingTasks} pending</span>
                            <span className="stat ongoing">⚙️ {project.ongoingTasks} ongoing</span>
                            <span className="stat">📋 {project.totalTasks} total</span>
                            <span className="stat">{project.completionRate}% complete</span>
                          </div>
                        </div>
                        <div className="rank-progress">
                          <div className="progress-bar-small">
                            <div className="progress-fill-small" style={{ width: `${project.completionRate}%`, background: getStatusColor(project.status) }}></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="appraisal-card">
              <div className="card-header">
                <h3>✅ Completed Projects</h3>
                <span className="card-badge success">{completedProjects.length} projects</span>
              </div>
              <div className="card-content">
                {completedProjects.length === 0 ? (
                  <div className="empty-message">No completed projects yet</div>
                ) : (
                  <div className="completed-projects-grid">
                    {completedProjects.map(project => (
                      <div key={project.id} className="completed-project-card">
                        <div className="project-name">{project.projectName}</div>
                        <div className="project-meta">
                          <span className="badge success">✅ {project.completedTasks}/{project.totalTasks} tasks</span>
                          <span className={`badge ${project.finishedOnTime ? 'success' : 'warning'}`}>
                            {project.finishedOnTime ? 'On Time' : 'Late'}
                          </span>
                        </div>
                        <div className="project-date">📅 Completed: {formatDate(project.endDateObj)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="two-columns">
              <div className="appraisal-card">
                <div className="card-header">
                  <h3>✅ On-Time Deliveries</h3>
                  <span className="card-badge success">{projectsFinishedOnTime}</span>
                </div>
                <div className="card-content">
                  <div className="progress-circle-container">
                    <div className="progress-circle" style={{ 
                      background: `conic-gradient(#10b981 0deg ${(projectsFinishedOnTime / Math.max(projectMetrics.length, 1)) * 360}deg, #e5e7eb ${(projectsFinishedOnTime / Math.max(projectMetrics.length, 1)) * 360}deg)`
                    }}>
                      <span className="circle-percentage">{Math.round((projectsFinishedOnTime / Math.max(projectMetrics.length, 1)) * 100)}%</span>
                    </div>
                  </div>
                  <div className="simple-list">
                    {projectMetrics.filter(p => p.finishedOnTime).slice(0, 5).map(project => (
                      <div key={project.id} className="simple-item">
                        <span className="item-name">{project.projectName}</span>
                        <span className="item-value success">✅ On Time</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="appraisal-card">
                <div className="card-header">
                  <h3>❌ Late Deliveries</h3>
                  <span className="card-badge warning">{projectsFinishedLate}</span>
                </div>
                <div className="card-content">
                  <div className="progress-circle-container">
                    <div className="progress-circle warning" style={{ 
                      background: `conic-gradient(#f59e0b 0deg ${(projectsFinishedLate / Math.max(projectMetrics.length, 1)) * 360}deg, #e5e7eb ${(projectsFinishedLate / Math.max(projectMetrics.length, 1)) * 360}deg)`
                    }}>
                      <span className="circle-percentage">{Math.round((projectsFinishedLate / Math.max(projectMetrics.length, 1)) * 100)}%</span>
                    </div>
                  </div>
                  <div className="simple-list">
                    {projectMetrics.filter(p => p.finishedLate).slice(0, 5).map(project => (
                      <div key={project.id} className="simple-item">
                        <span className="item-name">{project.projectName}</span>
                        <span className="item-value late">❌ Late</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="appraisal-card">
              <div className="card-header">
                <h3>🏆 Top Performers</h3>
                <span className="card-badge success">Highest On-Time Rate</span>
              </div>
              <div className="card-content">
                {topPerformers.length === 0 ? (
                  <div className="empty-message">No data available</div>
                ) : (
                  <div className="ranking-list">
                    {topPerformers.map((worker, index) => (
                      <div key={worker.id} className="ranking-item">
                        <div className="rank gold">{index + 1}</div>
                        <div className="rank-info">
                          <div className="rank-name">{worker.name || 'Unnamed'}</div>
                          <div className="rank-stats">
                            <span className="stat success">{worker.onTimeRate}% on time</span>
                            <span className="stat">✅ {worker.completedTasks} completed</span>
                            <span className="stat">📋 {worker.totalTasks} total</span>
                          </div>
                        </div>
                        <div className="rank-progress">
                          <div className="progress-bar-small">
                            <div className="progress-fill-small success" style={{ width: `${worker.onTimeRate}%` }}></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {workersWithOverdue.length > 0 && (
              <div className="appraisal-card">
                <div className="card-header">
                  <h3>⚠️ Workers With Overdue Tasks</h3>
                  <span className="card-badge danger">Needs Attention</span>
                </div>
                <div className="card-content">
                  <div className="simple-list">
                    {workersWithOverdue.map(worker => (
                      <div key={worker.id} className="simple-item">
                        <span className="item-name">{worker.name || 'Unnamed'}</span>
                        <div className="item-stats">
                          <span className="badge overdue">🔴 {worker.overdueTasks} overdue</span>
                          <span className="badge pending">📋 {worker.totalTasks - worker.completedTasks} pending</span>
                          <span className="badge success">✅ {worker.completedTasks} completed</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="appraisal-card">
              <div className="card-header">
                <h3>🏅 Most Productive Workers</h3>
                <span className="card-badge info">By Completed Tasks</span>
              </div>
              <div className="card-content">
                {mostProductive.filter(w => w.completedTasks > 0).length === 0 ? (
                  <div className="empty-message">No completed tasks yet</div>
                ) : (
                  <div className="ranking-list">
                    {mostProductive.filter(w => w.completedTasks > 0).map((worker, index) => (
                      <div key={worker.id} className="ranking-item">
                        <div className="rank">{index + 1}</div>
                        <div className="rank-info">
                          <div className="rank-name">{worker.name || 'Unnamed'}</div>
                          <div className="rank-stats">
                            <span className="stat success">✅ {worker.completedTasks} completed</span>
                            <span className="stat">📋 {worker.totalTasks} total</span>
                            {worker.onTimeRate !== null && (
                              <span className={`stat ${worker.onTimeRate >= 80 ? 'success' : 'warning'}`}>{worker.onTimeRate}% on time</span>
                            )}
                          </div>
                        </div>
                        <div className="rank-progress">
                          <div className="progress-bar-small">
                            <div className="progress-fill-small" style={{ width: `${(worker.completedTasks / Math.max(worker.totalTasks, 1)) * 100}%` }}></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="appraisal-card">
              <div className="card-header">
                <h3>📋 All Team Members Summary</h3>
                <span className="card-badge info">{workers.length} members</span>
              </div>
              <div className="card-content">
                <div className="workers-summary-grid">
                  {workerMetrics.map(worker => (
                    <div key={worker.id} className="worker-summary-card">
                      <div className="worker-avatar">{worker.name?.charAt(0).toUpperCase() || '?'}</div>
                      <div className="worker-summary-info">
                        <div className="worker-summary-name">{worker.name || 'Unnamed'}</div>
                        <div className="worker-summary-role">{worker.role?.replace('-', ' ') || 'Staff'}</div>
                        <div className="worker-summary-stats">
                          <span>📋 {worker.totalTasks}</span>
                          <span>✅ {worker.completedTasks}</span>
                          {worker.onTimeRate !== null && (
                            <span className={worker.onTimeRate >= 80 ? 'success-text' : 'warning-text'}>
                              {worker.onTimeRate}% on time
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="appraisal-footer">
        <div className="footer-note">
          <span>📊 Data based on completed and verified tasks</span>
          <span>🕐 On-time means completed before or on the due date</span>
          <span>📈 Last updated: {new Date().toLocaleString()}</span>
          <span>👥 Showing {workers.length} team members</span>
        </div>
      </div>
    </div>
  );
};

export default DataAppraisal;